import React, { useState, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDropzone } from 'react-dropzone';
import { useRefresh, useNotify } from 'react-admin';
import auth from '../../auth/auth0';
import { getApiUrlForResource } from '../../services/apiUrlResolver';
import { PARTNER_MARKETING_ASSETS_RESOURCE_NAME, PresignedUrlResponse } from '../partnerMarketingAssets.model';
import logger from '../../utils/logger';

const SUPPORTED_TYPES = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

interface UploadState {
  file: File | null;
  progress: number;
  status: 'idle' | 'uploading' | 'success' | 'error';
  error: string | null;
  publicUrl: string | null;
}

const AssetUploadButton = () => {
  const [open, setOpen] = useState(false);
  const [folderPath, setFolderPath] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [uploadState, setUploadState] = useState<UploadState>({
    file: null,
    progress: 0,
    status: 'idle',
    error: null,
    publicUrl: null,
  });

  const refresh = useRefresh();
  const notify = useNotify();

  const resetState = () => {
    setUploadState({
      file: null,
      progress: 0,
      status: 'idle',
      error: null,
      publicUrl: null,
    });
    setFolderPath('');
    setDisplayName('');
  };

  const handleOpen = () => {
    resetState();
    setOpen(true);
  };

  const handleClose = () => {
    if (uploadState.status === 'uploading') {
      return; // Don't close while uploading
    }
    setOpen(false);
    resetState();
    if (uploadState.status === 'success') {
      refresh();
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (!Object.keys(SUPPORTED_TYPES).includes(file.type)) {
        setUploadState(prev => ({
          ...prev,
          error: `Unsupported file type: ${file.type}. Supported: PDF, PNG, JPG, GIF, WebP`,
        }));
        return;
      }
      setUploadState(prev => ({
        ...prev,
        file,
        error: null,
      }));
      if (!displayName) {
        setDisplayName(file.name);
      }
    }
  }, [displayName]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
    maxFiles: 1,
    disabled: uploadState.status === 'uploading' || uploadState.status === 'success',
  });

  const handleUpload = async () => {
    if (!uploadState.file) return;

    const file = uploadState.file;
    const token = auth.getIdToken();
    const apiUrl = getApiUrlForResource(PARTNER_MARKETING_ASSETS_RESOURCE_NAME);

    // Declare presignedData outside try block so it's accessible in catch for cleanup
    let presignedData: PresignedUrlResponse | null = null;

    setUploadState(prev => ({ ...prev, status: 'uploading', progress: 10 }));

    try {
      // Step 1: Get presigned URL
      const presignedResponse = await fetch(
        `${apiUrl}/${PARTNER_MARKETING_ASSETS_RESOURCE_NAME}/presigned-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type,
            folder_path: folderPath,
          }),
        }
      );

      if (!presignedResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      presignedData = await presignedResponse.json();
      setUploadState(prev => ({ ...prev, progress: 30 }));

      // Step 2: Upload file directly to S3
      const uploadResponse = await fetch(presignedData.upload_url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text().catch(() => 'Unknown error');
        throw new Error(`Failed to upload file to storage: ${uploadResponse.status} ${errorText}`);
      }

      setUploadState(prev => ({ ...prev, progress: 70 }));

      // Step 3: Create asset record
      const createUrl = `${apiUrl}/${PARTNER_MARKETING_ASSETS_RESOURCE_NAME}`;
      const createResponse = await fetch(createUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          s3_key: presignedData.s3_key,
          original_filename: file.name,
          display_name: displayName || file.name,
          folder_path: folderPath,
          file_size_bytes: file.size,
          content_type: file.type,
        }),
      }).catch((fetchError) => {
        // Network error (CORS, connection refused, etc.)
        logger.error('Failed to create asset record - network error', {
          url: createUrl,
          error: fetchError.message,
        });
        throw new Error(`Failed to create asset record: ${fetchError.message}`);
      });

      if (!createResponse.ok) {
        // Clone response to allow reading body multiple times
        const responseClone = createResponse.clone();
        let errorMessage = `HTTP ${createResponse.status}`;

        // Try JSON first (API errors are typically JSON)
        try {
          const errorJson = await responseClone.json();
          errorMessage = errorJson.message || errorJson.error || JSON.stringify(errorJson);
        } catch {
          // If JSON parsing fails, try text as fallback
          try {
            const errorText = await createResponse.text();
            errorMessage = errorText || errorMessage;
          } catch {
            // If both fail, use status text or default message
            errorMessage = createResponse.statusText || errorMessage;
          }
        }
        logger.error('Failed to create asset record - API error', {
          status: createResponse.status,
          error: errorMessage,
        });
        throw new Error(`Failed to save asset record: ${createResponse.status} - ${errorMessage}`);
      }

      setUploadState(prev => ({
        ...prev,
        status: 'success',
        progress: 100,
        publicUrl: presignedData.public_url,
      }));

      notify('Asset uploaded successfully', { type: 'success' });

      // Refresh the list to show the new asset immediately
      refresh();

    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed';

      // If we have presignedData, we uploaded to S3 but failed to create the record
      // Clean up the orphaned file to prevent storage leaks
      if (presignedData?.s3_key) {
        try {
          await fetch(
            `${apiUrl}/${PARTNER_MARKETING_ASSETS_RESOURCE_NAME}/cleanup`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ s3_key: presignedData.s3_key }),
            }
          );
          logger.info('Cleaned up orphaned S3 file after failed record creation', {
            s3_key: presignedData.s3_key,
          });
        } catch (cleanupError) {
          logger.error('Failed to cleanup orphaned S3 file', {
            s3_key: presignedData.s3_key,
            error: cleanupError,
          });
        }
      }

      setUploadState(prev => ({
        ...prev,
        status: 'error',
        error: message,
      }));
      notify(message, { type: 'error' });
    }
  };

  const handleCopyUrl = () => {
    if (uploadState.publicUrl) {
      navigator.clipboard.writeText(uploadState.publicUrl);
      notify('URL copied to clipboard', { type: 'success' });
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<CloudUploadIcon />}
        onClick={handleOpen}
        size="small"
      >
        Upload Asset
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload Marketing Asset
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={uploadState.status === 'uploading'}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {uploadState.status === 'success' ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Upload Complete!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your asset is now available at:
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                  }}
                >
                  {uploadState.publicUrl}
                </Typography>
                <IconButton size="small" onClick={handleCopyUrl}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'grey.300',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: uploadState.status === 'uploading' ? 'not-allowed' : 'pointer',
                  bgcolor: isDragActive ? 'primary.light' : 'grey.50',
                  mb: 2,
                  opacity: uploadState.status === 'uploading' ? 0.5 : 1,
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
                {uploadState.file ? (
                  <Typography>
                    Selected: <strong>{uploadState.file.name}</strong> ({(uploadState.file.size / 1024).toFixed(1)} KB)
                  </Typography>
                ) : isDragActive ? (
                  <Typography>Drop the file here...</Typography>
                ) : (
                  <Typography>
                    Drag & drop a file here, or click to select
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Supported: PDF, PNG, JPG, GIF, WebP
                    </Typography>
                  </Typography>
                )}
              </Box>

              <TextField
                fullWidth
                label="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                margin="normal"
                helperText="Optional friendly name for the asset"
                disabled={uploadState.status === 'uploading'}
              />

              <TextField
                fullWidth
                label="Folder Path"
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                margin="normal"
                helperText="Optional folder for organization (e.g., 'partners/acme')"
                disabled={uploadState.status === 'uploading'}
              />

              {uploadState.status === 'uploading' && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={uploadState.progress} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Uploading... {uploadState.progress}%
                  </Typography>
                </Box>
              )}

              {uploadState.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {uploadState.error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          {uploadState.status === 'success' ? (
            <Button onClick={handleClose} variant="contained">
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={uploadState.status === 'uploading'}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                variant="contained"
                disabled={!uploadState.file || uploadState.status === 'uploading'}
              >
                {uploadState.status === 'uploading' ? 'Uploading...' : 'Upload'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssetUploadButton;
